import { Button, ButtonHoverSelector, ButtonKind, ButtonSize } from "@gadgetinc/widgets";
import { GitHubIcon } from "@gadgetinc/widgets/src/icons/GitHubIcon";
import { ThirdPartyIcon } from "@gadgetinc/widgets/src/icons/ThirdPartyIcon";
import { useStyletron } from "baseui";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { StyleObject } from "styletron-standard";
import { asyncSafeEventHandler } from "../../lib/utils";
import { useGadgetAuthConfig } from "./AuthLayout";
import { useLogin } from "./login.utils";

const OAuthSignInButton = observer((props: { children: React.ReactNode; icon: React.ReactNode; href: string }) => {
  const [_css, $theme] = useStyletron();
  const { bypassCfTurnstile } = useGadgetAuthConfig();
  const { form } = useLogin();

  return (
    <SignInButton
      href={props.href}
      onClick={asyncSafeEventHandler(async (event) => {
        event.preventDefault();

        await form.signInWithOAuth(props.href, bypassCfTurnstile);
      })}
      icon={props.icon}
      disabled={form.showTurnstile || form.loading}
    >
      {props.children}
    </SignInButton>
  );
});

export const SignInButton = observer(
  (props: {
    children: React.ReactNode;
    href: string;
    icon: React.ReactNode;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean;
    $style?: StyleObject;
  }) => {
    const [_css, $theme] = useStyletron();

    return (
      <Button
        href={props.href}
        onClick={props.onClick}
        startEnhancer={props.icon}
        kind={ButtonKind.tertiary}
        size={ButtonSize.large}
        disabled={props.disabled}
        $style={props.$style}
        overrides={{
          BaseButton: {
            style: {
              color: $theme.colors.contentPrimary,
              textDecoration: "none",
              backgroundColor: $theme.colors.alpha50,
              padding: $theme.sizing.scale300,
              [ButtonHoverSelector]: {
                backgroundColor: $theme.colors.alpha100,
                color: $theme.colors.contentPrimary,
                textDecoration: "none",
              },
              ":active:not(:disabled)": {
                backgroundColor: $theme.colors.alpha200,
                color: $theme.colors.contentPrimary,
                textDecoration: "none",
              },
              ":focus-visible": {
                color: $theme.colors.contentPrimary,
              },
            },
          },
        }}
      >
        {props.children}
      </Button>
    );
  }
);

export const GoogleSignInButton = observer(() => {
  return (
    <OAuthSignInButton href="/auth/google" icon={<ThirdPartyIcon type="google" $size={20} />}>
      Continue with Google
    </OAuthSignInButton>
  );
});
export const GitHubSignInButton = observer(() => {
  return (
    <OAuthSignInButton href="/auth/github" icon={<GitHubIcon $size={20} />}>
      Continue with GitHub
    </OAuthSignInButton>
  );
});
